import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ThemeProvider, CssBaseline } from '@mui/material'
import { createAppTheme, type ColorMode } from '@/theme'

const STORAGE_KEY = 'flixalio.colorMode'

interface ColorModeContextValue {
  mode: ColorMode
  toggle: () => void
  setMode: (mode: ColorMode) => void
}

const ColorModeContext = createContext<ColorModeContextValue | null>(null)

/** Dark is the design's default; a stored choice always wins. */
function readStoredMode(): ColorMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    // Private mode or a blocked storage API must not stop the app from rendering
  }
  return 'dark'
}

/**
 * Holds the light/dark choice, mirrors it onto <html data-theme> for the CSS
 * custom properties in styles/tokens.css, and hands MUI a matching theme.
 */
export function ColorModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ColorMode>(readStoredMode)

  useEffect(() => {
    document.documentElement.dataset.theme = mode
    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      // Not being able to remember the choice is not worth an error
    }
  }, [mode])

  const setMode = useCallback((next: ColorMode) => setModeState(next), [])
  const toggle = useCallback(
    () => setModeState((m) => (m === 'dark' ? 'light' : 'dark')),
    [],
  )

  const theme = useMemo(() => createAppTheme(mode), [mode])
  const value = useMemo(() => ({ mode, toggle, setMode }), [mode, toggle, setMode])

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  )
}

export function useColorMode(): ColorModeContextValue {
  const ctx = useContext(ColorModeContext)
  if (!ctx) throw new Error('useColorMode must be used inside ColorModeProvider')
  return ctx
}
